import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { NavBar, TabBar, Modal, Input, Button, Toast } from 'antd-mobile';
import {
  UserOutline,
  AppOutline,
  FileOutline,
  QuestionCircleOutline,
  TeamOutline,
} from 'antd-mobile-icons';
import api, { clearAuth } from './api';

import LoginPage from './pages/LoginPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import ProfilePage from './pages/ProfilePage';
import SpecialProjectsPage from './pages/SpecialProjectsPage';
import PhotoFixationPage from './pages/PhotoFixationPage';
import ZavhozPage from './pages/ZavhozPage';
import ZavhozRequestPage from './pages/ZavhozRequestPage';
import ZavhozNewPage from './pages/ZavhozNewPage';
import GoszadaniePage from './pages/GoszadaniePage';
import OfficePage from './pages/OfficePage';
import SatisfactionPage from './pages/SatisfactionPage';
import TestPage from './pages/TestPage';
import TestTaskPage from './pages/TestTaskPage';
import TestCodePage from './pages/TestCodePage';

const tabs = [
  { key: '/goszadanie', title: 'Госзадание', icon: <FileOutline /> },
  { key: '/special-projects', title: 'Спецпроекты', icon: <AppOutline /> },
  { key: '/test', title: 'Тест', icon: <QuestionCircleOutline /> },
  { key: '/office', title: 'Офис', icon: <TeamOutline /> },
  { key: '/profile', title: 'Профиль', icon: <UserOutline /> },
];

function MobileLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuth = !!localStorage.getItem('token');
  const [user, setUser] = useState(null);
  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      clearAuth();
      navigate('/login');
      return;
    }
    api
      .get('/auth/me', { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const parsed = res.data;
        if (parsed) {
          localStorage.setItem('user', JSON.stringify(parsed));
          setUser(parsed);
          if (parsed.must_change_password) {
            navigate('/change-password');
            return;
          }
          if (!parsed.email) {
            setEmailModalVisible(true);
          }
        }
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          Toast.show({ icon: 'fail', content: 'Сессия истекла. Войдите заново.' });
        }
        clearAuth();
        navigate('/login');
      });
  }, [navigate]);

  const handleSaveEmail = async () => {
    const email = emailInput.trim();
    if (!email || !email.includes('@')) {
      Toast.show({ icon: 'fail', content: 'Введите корректный email' });
      return;
    }
    setSavingEmail(true);
    try {
      const token = localStorage.getItem('token');
      const res = await api.patch('/auth/me/email', { email }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const updated = { ...user, email: res.data.email };
      localStorage.setItem('user', JSON.stringify(updated));
      setUser(updated);
      setEmailModalVisible(false);
      Toast.show({ icon: 'success', content: 'Email сохранён' });
    } catch (err) {
      if (err?.response?.status === 401) {
        Toast.show({ icon: 'fail', content: 'Сессия истекла. Войдите заново.' });
        clearAuth();
        navigate('/login');
        return;
      }
      const detail = err?.response?.data?.detail || 'Ошибка сохранения email';
      Toast.show({ icon: 'fail', content: detail });
    } finally {
      setSavingEmail(false);
    }
  };

  const handleLogoutFromModal = () => {
    clearAuth();
    setEmailModalVisible(false);
    navigate('/login');
  };

  if (!isAuth) return <Navigate to="/login" replace />;

  return (
    <div style={{ paddingBottom: 60 }}>
      <NavBar back={null} style={{ background: '#1890ff', color: '#fff' }}>
        Ботаник Go
      </NavBar>
      <div style={{ padding: 12 }}>
        <Routes>
          <Route path="/" element={<Navigate to="/goszadanie" replace />} />
          <Route path="/goszadanie" element={<GoszadaniePage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/special-projects" element={<SpecialProjectsPage />} />
          <Route path="/special-projects/photo-fixation" element={<PhotoFixationPage />} />
          <Route path="/zavhoz" element={<ZavhozPage />} />
          <Route path="/zavhoz/new" element={<ZavhozNewPage />} />
          <Route path="/zavhoz/:id" element={<ZavhozRequestPage />} />
          <Route path="/test" element={<TestPage />} />
          <Route path="/office" element={<OfficePage />} />
          <Route path="/satisfaction" element={<SatisfactionPage />} />
          <Route path="/test/:taskId" element={<TestTaskPage />} />
          <Route path="/test/:taskId/code/:index" element={<TestCodePage />} />
        </Routes>
      </div>
      <TabBar
        activeKey={location.pathname}
        onChange={(key) => navigate(key)}
        style={{ position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: 430, background: '#fff', borderTop: '1px solid #eee' }}
      >
        {tabs.map((item) => (
          <TabBar.Item key={item.key} icon={item.icon} title={item.title} />
        ))}
      </TabBar>

      <Modal
        visible={emailModalVisible}
        transparent
        maskClosable={false}
        title="Укажите email"
        footer={[
          {
            text: 'Выйти',
            onPress: handleLogoutFromModal,
          },
          {
            text: 'Сохранить',
            onPress: handleSaveEmail,
          },
        ]}
      >
        <div style={{ marginBottom: 12 }}>
          Для входа в следующий раз и получения уведомлений необходимо указать корпоративный email.
        </div>
        <Input
          type="email"
          placeholder="ivanov.ii@str.mos.ru"
          value={emailInput}
          onChange={(v) => setEmailInput(v)}
          clearable
        />
        {savingEmail && (
          <div style={{ textAlign: 'center', marginTop: 12, color: '#999' }}>Сохранение...</div>
        )}
      </Modal>
    </div>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/change-password" element={<ChangePasswordPage />} />
      <Route path="/*" element={<MobileLayout />} />
    </Routes>
  );
}

export default App;
